// src/components/dashboard/client/ClientNotificationDetail.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import {
  ArrowLeft,
  Send,
  Eye
} from 'lucide-react';

const ClientNotificationDetail = () => {
  const { dossierId, notificationId } = useParams();
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [dossierData, setDossierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newReply, setNewReply] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const currentUser = auth.currentUser;

  useEffect(() => {
    const loadData = async () => {
      if (!dossierId || !notificationId) return;

      try {
        const dossierDoc = await getDoc(doc(db, "dossiers", dossierId));
        if (dossierDoc.exists()) {
          setDossierData(dossierDoc.data());
        }

        const unsubscribe = onSnapshot(
          doc(db, "notifications", notificationId),
          async (notificationDoc) => {
            if (notificationDoc.exists()) {
              const data = notificationDoc.data();
              setNotification({ id: notificationDoc.id, ...data });

              if (currentUser && !data.readBy?.includes(currentUser.uid)) {
                await updateDoc(doc(db, "notifications", notificationId), {
                  readBy: arrayUnion(currentUser.uid)
                });
                console.log(`✅ Notification ${notificationId} marquée comme lue par ${currentUser.uid}`);
              }
            }
            setLoading(false);
          },
          (error) => {
            console.error("Erreur lors du chargement de la notification:", error);
            setLoading(false);
          }
        );

        return unsubscribe;
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        setLoading(false);
      }
    };

    const unsubscribe = loadData();
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [dossierId, notificationId, currentUser]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [notification?.replies]);

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!newReply.trim() || !currentUser || sending) return;

    setSending(true);
    try {
      const replyId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random()}`;
      const reply = {
        id: replyId,
        message: newReply.trim(),
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email || 'Client',
        createdAt: Timestamp.now(),
        readBy: [currentUser.uid]
      };

      await updateDoc(doc(db, "notifications", notificationId), {
        replies: arrayUnion(reply)
      });

      setNewReply('');
    } catch (error) {
      console.error("Erreur lors de l'envoi de la réponse:", error);
      alert("Erreur lors de l'envoi de la réponse. Veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return '';
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getUserColor = (createdBy) => {
    return createdBy === currentUser?.uid
      ? 'bg-blue-50 border border-blue-200'
      : 'bg-gray-50 border border-gray-200';
  };

  const getUserRole = (createdByName, createdBy) => {
    if (createdBy === currentUser?.uid) return 'Client';
    if (createdByName?.toLowerCase().includes('admin') || createdByName?.toLowerCase().includes('super')) {
      return 'Super Admin';
    }
    if (createdByName?.toLowerCase().includes('charge')) {
      return 'Chargé d\'affaires';
    }
    if (createdByName?.toLowerCase().includes('chef')) {
      return 'Chef de projet';
    }
    return 'Équipe';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!notification || !dossierData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Notification non trouvée</h2>
          <button
            onClick={() => navigate(`/clients/${dossierId}`)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/clients/${dossierId}`)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {dossierData.titre}
              </h1>
              <p className="text-sm text-gray-600">Discussion avec l'équipe</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900 mb-2">{notification.title}</h2>
            <div className="text-sm text-gray-500 mb-4">
              {formatDate(notification.createdAt)}
            </div>
            <div className="prose max-w-none">
              <p className="whitespace-pre-line text-gray-700">{notification.message}</p>
            </div>
          </div>

          <div className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">
              Discussion ({notification.replies?.length || 0} messages)
            </h3>

            <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
              {notification.replies?.map((reply, index) => {
                const isCurrentUser = reply.createdBy === currentUser?.uid;
                const userRole = getUserRole(reply.createdByName, reply.createdBy);

                return (
                  <div key={reply.id || index} className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                        {isCurrentUser ? 'C' : 'S'}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{userRole}</div>
                        <div className="text-xs text-gray-500">{formatDate(reply.createdAt)}</div>
                      </div>
                    </div>

                    <div className={`ml-10 p-3 rounded-lg ${getUserColor(reply.createdBy)}`}>
                      <p className="whitespace-pre-wrap text-gray-900">{reply.message}</p>
                      <div className="flex items-center space-x-2 mt-2 text-xs text-gray-500">
                        <Eye className="w-3 h-3" />
                        <span>Lu par {reply.readBy?.length || 0}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendReply} className="space-y-3">
              <div className="flex space-x-3">
                <textarea
                  value={newReply}
                  onChange={(e) => setNewReply(e.target.value)}
                  placeholder="Tapez votre message..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows="3"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply(e);
                    }
                  }}
                />
                <button
                  type="submit"
                  disabled={!newReply.trim() || sending}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  {sending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  <span>{sending ? 'Envoi...' : 'Envoyer'}</span>
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Appuyez sur Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientNotificationDetail;
